import { observer } from "mobx-react";
import { LLMClassifierLibraryStore } from "../Stores/LLMClassifierLibraryStore";
import { useStore } from "utils/useStore";
import { useCallback, useEffect, useMemo } from "react";
import React from "react";
import {
    Divider,
    Grid,
    ListItemIcon,
    Paper,
    Stack,
    styled,
    Tooltip,
    Typography,
} from "@mui/material";
import { LLMClassifierForLibrary, LLMClassifierGrouping } from "models/LLMs";
import {
    CheckCircleOutlineRounded,
    KeyboardArrowDown,
    Search,
    SubdirectoryArrowRight,
} from "@mui/icons-material";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import moment from "moment";
import AcxEmptyState from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import { LLMPurposeIcon } from "../Components/LLMPurposeIcon";
import { AuthStore } from "stores/AuthStore";
import AcxMenu, {
    AcxMenuItemProps,
    StyledMenuLabel,
} from "components/UI/Menu/AcxMenu";

const LibraryGroupHeader = styled(Typography)(({ theme }) => ({
    color: theme.palette.gray[700],
    fontWeight: 600,
    fontSize: "18px",
    lineHeight: "28px",
}));

const LibrarySubGroupHeader = styled(Typography)(({ theme }) => ({
    color: theme.palette.gray[700],
    fontWeight: 600,
    fontSize: "15px",
    lineHeight: "24px",
}));

const isDefined = <T,>(value: T | undefined): value is T => value !== undefined;

export const LLMClassifierLibrary = observer(() => {
    const authStore = useStore(AuthStore);
    const store = useStore(LLMClassifierLibraryStore);
    const [searchString, setSearchString] = React.useState("");

    useEffect(() => {
        store.loadLibrary();
    }, [store]);

    const classifierMatchesSearch = useCallback(
        (llmClassifier: LLMClassifierForLibrary) => {
            const normalizedSearch = searchString.toLowerCase();
            return (
                llmClassifier.name.toLowerCase().includes(normalizedSearch) ||
                llmClassifier.description
                    ?.toLowerCase()
                    .includes(normalizedSearch)
            );
        },
        [searchString],
    );

    const filteredLibrary = useMemo(() => {
        const filterGroup = (
            group: LLMClassifierGrouping,
        ): LLMClassifierGrouping | undefined => {
            const llmClassifiers = group.llmClassifiers.filter(
                classifierMatchesSearch,
            );
            const subGroups = (group.subGroups ?? [])
                .map(filterGroup)
                .filter(isDefined);

            if (llmClassifiers.length === 0 && subGroups.length === 0) {
                return undefined;
            }

            return {
                ...group,
                llmClassifiers,
                subGroups,
            };
        };

        return store.library.map(filterGroup).filter(isDefined);
    }, [classifierMatchesSearch, store.library]);

    const isLoading = store.getTaskLoading(
        LLMClassifierLibraryStore.Tasks.GetLLMClassifierLibrary,
    );

    const canUserAdd = authStore.canUserEdit("Model Builder");

    return (
        <Grid
            container
            item
            direction="column"
            rowGap={5}
            sx={(theme) => ({
                height: "100%",
                padding: theme.spacing(5, 10, 10, 10),
            })}
            flexWrap="nowrap"
        >
            <Grid
                container
                item
                justifyContent="space-between"
                alignItems="center"
            >
                <Grid item>
                    <Typography variant="h1">Model Library</Typography>
                </Grid>
                <Grid item>
                    <AcxMainTextField
                        id="model-library-search"
                        placeholderText="Search models..."
                        startAdornment={
                            <Search
                                sx={{
                                    marginRight: "0.5rem",
                                    color: "gray.400",
                                }}
                                fontSize="small"
                            />
                        }
                        value={searchString}
                        onChange={(event) => {
                            const search = event.target.value;
                            setSearchString(search);
                        }}
                    />
                </Grid>
            </Grid>
            {isLoading && <AcxEmptyState variant="loading" size="large" />}
            {!isLoading &&
                filteredLibrary.map((grouping) => (
                    <LibraryGroup
                        grouping={grouping}
                        canUserAdd={canUserAdd}
                        key={grouping.groupId}
                    />
                ))}
        </Grid>
    );
});

const LibraryGroup = observer(
    ({
        grouping,
        canUserAdd,
        depth = 0,
    }: {
        grouping: LLMClassifierGrouping;
        canUserAdd: boolean;
        depth?: number;
    }) => {
        const store = useStore(LLMClassifierLibraryStore);
        const actionGroup = store.findGroup(grouping.groupId) ?? grouping;
        const Header = depth === 0 ? LibraryGroupHeader : LibrarySubGroupHeader;

        return (
            <Grid
                container
                item
                direction="column"
                rowGap={2}
                flexWrap="nowrap"
            >
                <Grid
                    container
                    item
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Grid item>
                        <Stack
                            direction="row"
                            columnGap={1}
                            alignItems="flex-end"
                        >
                            {depth > 0 && <SubdirectoryArrowRight />}
                            <Header>{grouping.groupName}</Header>
                        </Stack>
                    </Grid>
                    {canUserAdd && (
                        <Grid item>
                            <GroupAddButton group={actionGroup} />
                        </Grid>
                    )}
                </Grid>
                {grouping.llmClassifiers.length > 0 && (
                    <Grid container item spacing={2} columns={4}>
                        {grouping.llmClassifiers.map((classifier) => (
                            <Grid item xs={1} key={classifier.id}>
                                <LibraryCard
                                    classifier={classifier}
                                    canUserAdd={canUserAdd}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
                {(grouping.subGroups ?? []).map((subGroup) => (
                    <LibraryGroup
                        grouping={subGroup}
                        canUserAdd={canUserAdd}
                        depth={depth + 1}
                        key={subGroup.groupId}
                    />
                ))}
            </Grid>
        );
    },
);

const GroupAddButton = observer(
    ({ group }: { group: LLMClassifierGrouping }) => {
        const store = useStore(LLMClassifierLibraryStore);
        const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(
            null,
        );

        const hasSubGroups = (group.subGroups ?? []).length > 0;
        const directClassifiers =
            LLMClassifierLibraryStore.getClassifiersForGroup(group, false);
        const allClassifiers = LLMClassifierLibraryStore.getClassifiersForGroup(
            group,
            true,
        );

        const directLoading = store.getTaskLoading(
            LLMClassifierLibraryStore.Tasks.GroupAddFromLibrary(
                group.groupId,
                false,
            ),
        );
        const childrenLoading = store.getTaskLoading(
            LLMClassifierLibraryStore.Tasks.GroupAddFromLibrary(
                group.groupId,
                true,
            ),
        );
        const directDisabled =
            directClassifiers.length === 0 ||
            directClassifiers.every((c) => c.existsInTenant) ||
            directClassifiers.some((c) =>
                store.getTaskLoading(
                    LLMClassifierLibraryStore.Tasks.SingleAddFromLibrary(c.id),
                ),
            );
        const childrenDisabled =
            allClassifiers.length === 0 ||
            allClassifiers.every((c) => c.existsInTenant) ||
            allClassifiers.some((c) =>
                store.getTaskLoading(
                    LLMClassifierLibraryStore.Tasks.SingleAddFromLibrary(c.id),
                ),
            );

        const addGroup = (includeChildGroups: boolean) => {
            setAnchorEl(null);
            store.addGroupFromLibrary(group.groupId, includeChildGroups);
        };

        const menuItems: AcxMenuItemProps[] = [
            {
                id: `${group.groupId}-direct`,
                label: (
                    <StyledMenuLabel>Add models in this group</StyledMenuLabel>
                ),
                props: {
                    disabled: directDisabled,
                    onClick: () => addGroup(false),
                },
            },
            {
                id: `${group.groupId}-children`,
                label: (
                    <StyledMenuLabel>
                        Add models in this group and sub groups
                    </StyledMenuLabel>
                ),
                props: {
                    disabled: childrenDisabled,
                    onClick: () => addGroup(true),
                },
            },
        ];

        if (!hasSubGroups) {
            return (
                <AcxButton
                    color="white"
                    disabled={directDisabled}
                    loading={directLoading}
                    disabledTooltip="All models in this group have already been added to Your Models"
                    onClick={() => addGroup(false)}
                >
                    Add All
                </AcxButton>
            );
        }

        return (
            <>
                <AcxButton
                    color="white"
                    disabled={
                        (directDisabled && childrenDisabled) ||
                        directLoading ||
                        childrenLoading
                    }
                    loading={directLoading || childrenLoading}
                    disabledTooltip="All models in this group have already been added to Your Models"
                    endIcon={<KeyboardArrowDown fontSize="small" />}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                >
                    Add All
                </AcxButton>
                <AcxMenu
                    anchorElement={anchorEl}
                    menuItems={menuItems}
                    onMenuClose={() => setAnchorEl(null)}
                />
            </>
        );
    },
);

const LLMClassifierName = styled(Typography)(({ theme }) => ({
    color: theme.palette.gray[700],
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
}));

const LLMClassifierDescription = styled(Typography)(({ theme }) => ({
    color: theme.palette.neutral[600],
    fontSize: "13px",
    lineHeight: "20px",
    fontWeight: 500,
}));

const LLMClassifierDetails = styled(Typography)(({ theme }) => ({
    color: theme.palette.gray[500],
    fontSize: "12px",
    lineHeight: "16px",
}));

const LibraryCard = observer(
    ({
        classifier,
        canUserAdd,
    }: {
        classifier: LLMClassifierForLibrary;
        canUserAdd: boolean;
    }) => {
        const store = useStore(LLMClassifierLibraryStore);

        return (
            <Paper
                sx={{
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "gray.200",
                }}
            >
                <Grid container direction="column">
                    <Grid container item p={2} direction="column" rowGap={2}>
                        <Grid
                            container
                            item
                            justifyContent="space-between"
                            alignItems="flex-start"
                        >
                            <Grid item>
                                <ListItemIcon
                                    sx={{
                                        borderRadius: 1,
                                        p: 1,
                                        width: "fit-content",
                                        minWidth: 0,
                                        background:
                                            "linear-gradient(132deg, #3664D6 3.97%, #1C3470 170.3%)",
                                        mr: 2,
                                    }}
                                >
                                    <LLMPurposeIcon
                                        purpose={classifier.purpose}
                                    />
                                </ListItemIcon>
                            </Grid>
                            {classifier.existsInTenant && (
                                <Tooltip title="This model has already been added to Your Models">
                                    <CheckCircleOutlineRounded
                                        sx={(theme) => ({
                                            color: theme.palette.primary[300],
                                        })}
                                    />
                                </Tooltip>
                            )}
                        </Grid>
                        <Grid container item direction="column" rowGap={1}>
                            <Grid item>
                                <LLMClassifierName>
                                    {classifier.name}
                                </LLMClassifierName>
                            </Grid>
                            <Grid item>
                                <LLMClassifierDescription>
                                    {classifier.description ?? "No description"}
                                </LLMClassifierDescription>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Divider />
                    <Grid
                        container
                        item
                        p={2}
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Grid item>
                            <LLMClassifierDetails>
                                Version {classifier.version} |{" "}
                                {moment
                                    .utc(classifier.modifiedOn)
                                    .local()
                                    .format("M/D/YY")}
                            </LLMClassifierDetails>
                        </Grid>
                        {canUserAdd && (
                            <Grid item>
                                <AcxButton
                                    disabled={
                                        classifier.existsInTenant ||
                                        store.isClassifierAffectedByGroupAddLoading(
                                            classifier.id,
                                        )
                                    }
                                    loading={store.getTaskLoading(
                                        LLMClassifierLibraryStore.Tasks.SingleAddFromLibrary(
                                            classifier.id,
                                        ),
                                    )}
                                    color="white"
                                    sx={{ minWidth: 0, px: 1, py: 0 }}
                                    disabledTooltip={
                                        classifier.existsInTenant
                                            ? "This model has already been added to Your Models"
                                            : "Models are being added to Your Models"
                                    }
                                    onClick={() =>
                                        store.addFromLibrary(classifier)
                                    }
                                >
                                    Add
                                </AcxButton>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        );
    },
);
